import { useHistory } from "react-router";
import { common_utils } from "../../../../../../../../../common/common_utils";
import { RouterInferenceHelpers } from "../../../../../../../../../src/api/trpc/rootRouter";
import { trpc } from "../../../../../../utils/trpc";
import { BackButton } from "../../../../base_components/back_button";
import { DashboardContainer } from "../../../../base_components/dashboard_container";
import { CopyableText } from "../../../../copyable_text";
import { Disabler } from "../../../../disabler";
import { ErrorNote } from "../../../../error_note";
import { useBackLink } from "../../../../hooks/use_back_link";
import { useServerId } from "../../../../hooks/use_server_id";
import { SettingsBooleanItem } from "../../../../settings_items/settings_boolean_item";

export function CalendarSettingsPage() {
	const history = useHistory();
	const back_link = useBackLink();
	const server_id = useServerId();

	const isAdminQuery = trpc.members.isAdmin.useQuery({
		guild_id: server_id,
	});

	const calendar_settings_query = trpc.calendar.settings.useQuery({
		guild_id: server_id,
	});

	return (
		<DashboardContainer
			is_loading={
				!isAdminQuery.isSuccess || !calendar_settings_query.isSuccess
			}
			bread_crumbs={
				<BackButton
					label={"Back"}
					onClick={() =>
						history.push(
							back_link ??
								common_utils.getGuildSettingsURL(
									server_id,
									"event"
								)
						)
					}
				/>
			}
			title={"Calendar Settings"}
		>
			{!isAdminQuery.data && (
				<ErrorNote
					error={
						<>
							Must have <span>Manage Sesh</span> permissions to
							change sesh settings
						</>
					}
				/>
			)}
			<Disabler disabled={!isAdminQuery.data}>
				<div className="flex flex-col gap-2">
					<PublicCalendarSetting
						settings={calendar_settings_query.data}
					/>
					<ShowNativeEventsSetting
						settings={calendar_settings_query.data}
					/>
				</div>
			</Disabler>
		</DashboardContainer>
	);
}

function PublicCalendarSetting(props: {
	settings: RouterInferenceHelpers["calendar"]["settings"]["output"];
}) {
	const server_id = useServerId();
	const context = trpc.useContext();

	const mutation = trpc.calendar.updateSettings.useMutation({
		onSuccess: (data) => {
			context.calendar.settings.setData(
				(prev) => {
					return {
						...prev,
						...data,
					};
				},
				{
					guild_id: server_id,
				}
			);
		},
	});
	return (
		<SettingsBooleanItem
			label="Publicly Accessible"
			description={
				<div className="flex flex-col gap-4">
					<p>
						Whether to make the calendar for this server publicly
						accessible. If enabled, users will not be required to
						log in to view this server's calendar.
					</p>
					<span>
						Public URL:{" "}
						<CopyableText
							text={
								props.settings.is_public
									? `https://sesh.fyi${common_utils.getGuildPublicCalendarURL(
											server_id
									  )}`
									: "Disabled"
							}
							is_disabled={!props.settings.is_public}
						/>
					</span>
					<p className="text-warning">
						Enabling this will reveal all events in all channels to
						everyone with the public URL.
					</p>
				</div>
			}
			is_loading={mutation.isLoading}
			onChange={(val) =>
				mutation.mutate({ is_public: val, guild_id: server_id })
			}
			value={props.settings.is_public}
		/>
	);
}

function ShowNativeEventsSetting(props: {
	settings: RouterInferenceHelpers["calendar"]["settings"]["output"];
}) {
	const server_id = useServerId();
	const context = trpc.useContext();

	const mutation = trpc.calendar.updateSettings.useMutation({
		onSuccess: (data) => {
			context.calendar.settings.setData(
				(prev) => {
					return {
						...prev,
						...data,
					};
				},
				{
					guild_id: server_id,
				}
			);

			context.event.list.invalidate();
		},
	});
	return (
		<SettingsBooleanItem
			label="Show Discord Native Events"
			description={
				<div className="flex flex-col gap-4">
					<p>
						Whether to show Discord native events on this calendar.
						For more info on Discord events, see{" "}
						<a
							className="manual-link"
							href="https://support.discord.com/hc/en-us/articles/4409494125719-Scheduled-Events"
						>
							here.
						</a>
					</p>
				</div>
			}
			is_loading={mutation.isLoading}
			onChange={(val) =>
				mutation.mutate({
					show_native_events: val,
					guild_id: server_id,
				})
			}
			value={props.settings.show_native_events}
		/>
	);
}
